import { useState } from "react";
import { ExternalLinkIcon } from "@radix-ui/react-icons";

import { userCancelTransaction } from "@/requests/order";

type TransactionRowProps = {
  index: number;
  transactionId: string;
  status: string;
  amount: number;
  date: string;
  wallet: string;
  rightSideLabel: string;
  rightSideValue: string;
  leftSideLabel: string;
  leftSideValue: string;
  paymentId: string;
  refreshTransactions: () => void;
};

const statusColor = (status: string) => {
  const lowerStatus = status.toLowerCase();
  if (lowerStatus.includes("completed"))
    return "text-green-500 border-green-500";
  if (lowerStatus.includes("expired")) return "text-red-500 border-red-500";
  if (lowerStatus.includes("processing"))
    return "text-yellow-500 border-yellow-500";
  if (lowerStatus.includes("sending funds"))
    return "text-yellow-500 border-yellow-500";
  if (lowerStatus.includes("cancelled")) return "text-red-500 border-red-500";
  if (lowerStatus.includes("waiting"))
    return "text-yellow-500 border-yellow-500";
  if (lowerStatus.includes("pending"))
    return "text-yellow-500 border-yellow-500";
  return "text-yellow-500 border-yellow-500";
};

const getStatusText = (status: string) => {
  const lowerStatus = status.toLowerCase();
  if (lowerStatus.includes("complete")) return "Completed";
  if (lowerStatus.includes("processing request money transfer"))
    return "Processing";
  if (lowerStatus.includes("expired")) return "Expired";
  if (lowerStatus.includes("cancelled")) return "Cancelled";
  if (lowerStatus.includes("sending funds")) return "Sending Funds";
  if (lowerStatus.includes("waiting")) return "Waiting";
  if (lowerStatus.includes("pending")) return "Pending";
  return status;
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  };
  return date.toLocaleDateString("en-US", options);
};

const getConfirmationUrl = (leftSideLabel: string, rightSideLabel: string) => {
  const labels = [leftSideLabel, rightSideLabel];
  if (
    labels.some((label) =>
      ["USDT-BSC", "BUSD-BSC", "USDC-BSC", "USDT-B", "BUSD-B"].includes(label)
    )
  ) {
    return "https://bscscan.com/";
  } else if (
    labels.some((label) =>
      ["USDC-POLY", "CADC-POLY", "USDC-P", "CADC-P"].includes(label)
    )
  ) {
    return "https://polygonscan.com/";
  } else if (
    labels.some((label) =>
      ["USDC-ARB", "CADC-ARB"].includes(label)
    )
  ) {
    return "https://arbiscan.io/";
  } else if (labels.includes("USDC-OP")) {
    return "https://optimistic.etherscan.io/";
  } else if (labels.includes("USDC-AVAX")) {
    return "https://snowtrace.io/";
  } else if (labels.includes("USDC-FTM")) {
    return "https://ftmscan.com/";
  } else if (labels.includes("USDC-GNO")) {
    return "https://gnosisscan.io/";
  } else if (labels.includes("USDC-CELO")) {
    return "https://celoscan.io/";
  } else if (labels.includes("USDC-SX")) {
    return "https://explorer.sx.technology/";
  } else if (
    labels.some((label) =>
      ["USDC-BASE", "CADC-BASE"].includes(label)
    )
  ) {
    return "https://basescan.org/";
  } else if (labels.includes("USDC-SOL")) {
    return "https://solscan.io/";
  } else if (
    labels.some((label) =>
      ["USDC-ETH", "CADC-ETH"].includes(label)
    )
  ) {
    return "https://etherscan.io/";
  } else {
    return "https://etherscan.io/";
  }
};

const truncateMiddle = (str: string, maxLength: number) => {
  if (str.length <= maxLength) return str;
  const halfLength = Math.floor((maxLength - 3) / 2);
  return (
    str.substring(0, halfLength) +
    "..." +
    str.substring(str.length - halfLength)
  );
};

const getNetwork = (leftSideLabel: string, rightSideLabel: string) => {
  const labels = [leftSideLabel, rightSideLabel];
  if (
    labels.some((label) =>
      ["USDT-BSC", "BUSD-BSC", "USDC-BSC", "USDT-B", "BUSD-B"].includes(label)
    )
  ) {
    return "Binance Smart Chain";
  } else if (
    labels.some((label) =>
      ["USDC-POLY", "CADC-POLY", "USDC-P", "CADC-P"].includes(label)
    )
  ) {
    return "Polygon";
  } else if (
    labels.some((label) =>
      ["USDC-ARB", "CADC-ARB"].includes(label)
    )
  ) {
    return "Arbitrum";
  } else if (labels.includes("USDC-OP")) {
    return "Optimism";
  } else if (labels.includes("USDC-AVAX")) {
    return "Avalanche";
  } else if (labels.includes("USDC-FTM")) {
    return "Fantom";
  } else if (labels.includes("USDC-GNO")) {
    return "Gnosis";
  } else if (labels.includes("USDC-CELO")) {
    return "Celo";
  } else if (labels.includes("USDC-SX")) {
    return "SX Network";
  } else if (
    labels.some((label) =>
      ["USDC-BASE", "CADC-BASE"].includes(label)
    )
  ) {
    return "Base";
  } else if (labels.includes("USDC-SOL")) {
    return "Solana";
  } else if (
    labels.some((label) =>
      ["USDC-ETH", "CADC-ETH"].includes(label)
    )
  ) {
    return "Ethereum";
  } else {
    return "Ethereum";
  }
};

export const TransactionRow = ({
  index,
  transactionId,
  status,
  amount,
  date,
  wallet,
  rightSideLabel,
  rightSideValue,
  leftSideLabel,
  leftSideValue,
  paymentId,
  refreshTransactions,
}: TransactionRowProps) => {
  const [isCancelConfirmVisible, setIsCancelConfirmVisible] = useState(false);

  const handleCancelTransaction = async () => {
    try {
      const response = await userCancelTransaction(transactionId);
      if (response.status === 200) {
        refreshTransactions();
      } else {
        console.error("Error cancelling transaction:", response.data);
      }
    } catch (error) {
      console.error("Error cancelling transaction:", error);
    } finally {
      setIsCancelConfirmVisible(false);
    }
  };

  const showCancelButton =
    status.toLowerCase().startsWith("pending") ||
    status.toLowerCase().startsWith("waiting");

  return (
    <>
      <tr className="cursor-pointer" onClick={() => showTXNDetails(index)} data-testid={`row-transaction-${index}`}>
        <td className="text-sm py-3 sm:py-6 border-b border-gray-300 px-2 break-all" data-testid={`cell-transaction-${index}-txid`}>
          {transactionId}
        </td>
        <td className="text-sm py-3 sm:py-6 border-b border-gray-300 text-left break-all" data-testid={`cell-transaction-${index}-status`}>
          <span
            className={`inline-block border ${statusColor(getStatusText(status))} text-center py-2 my-2 break-words whitespace-normal`}
            style={{
              minWidth: "12ch",
              maxWidth: "15ch",
              wordWrap: "break-word",
              whiteSpace: "normal",
            }}
            data-testid={`indicator-transaction-${index}-status`}
          >
            {getStatusText(status)}
          </span>
        </td>
        <td className="text-sm py-3 sm:py-6 border-b border-gray-300 text-right lg:text-center sm:text-center break-all" data-testid={`cell-transaction-${index}-amount`}>
          {amount}
        </td>
      </tr>
      <tr id={`panel${index}`} className="hidden bg-cloudDarker" data-testid={`container-transaction-detail-${index}`}>
        <td colSpan={3} className="dark:bg-darkCharcoal py-2 sm:py-4 px-2">
          <div className="text-sm ml-2 p-1 sm:p-2 rounded border-gray-300" data-testid={`container-transaction-detail-content-${index}`}>
            <div className="flex flex-col font-bold sm:flex-row justify-between py-1" data-testid={`container-transaction-detail-date-${index}`}>
              <span className="font-bold">Date:</span>{" "}
              <span className="font-normal">{formatDate(date)}</span>
            </div>
            <div className="flex flex-col font-bold sm:flex-row justify-between py-1" data-testid={`container-transaction-detail-wallet-${index}`}>
              <strong>Wallet:</strong>
              <span className="font-normal">
                <a
                  href={`${getConfirmationUrl(leftSideLabel, rightSideLabel)}address/${wallet}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block sm:inline break-all whitespace-normal text-blue-500"
                  style={{ wordBreak: "break-word", maxWidth: "42ch" }}
                  data-testid={`link-transaction-detail-wallet-${index}`}
                >
                  {truncateMiddle(wallet, 66)}
                </a>
              </span>
            </div>
            <div className="flex flex-col font-bold sm:flex-row justify-between py-1" data-testid={`container-transaction-detail-pay-${index}`}>
              <strong>You Pay:</strong>{" "}
              <span className="font-normal">
                {leftSideValue} {leftSideLabel}
              </span>
            </div>
            <div className="flex flex-col font-bold sm:flex-row justify-between py-1" data-testid={`container-transaction-detail-receive-${index}`}>
              <strong>You Receive:</strong>{" "}
              <span className="font-normal">
                {rightSideValue} {rightSideLabel}
              </span>
            </div>
            <div className="flex flex-col font-bold sm:flex-row justify-between py-1" data-testid={`container-transaction-detail-network-${index}`}>
              <strong>Network:</strong>
              <span className="font-normal">
                {getNetwork(leftSideLabel, rightSideLabel)}
              </span>
            </div>
            <div className="flex flex-col font-bold sm:flex-row justify-between py-1" data-testid={`container-transaction-detail-status-${index}`}>
              <strong>Status:</strong>
              <span className="font-normal">
                {getStatusText(status)}
                <span>
                  <a
                    href={`https://app.paytrie.com/Status?id=${transactionId}`}
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:underline"
                    target="_blank"
                    data-testid={`link-transaction-detail-status-${index}`}
                  >
                    <ExternalLinkIcon className="inline-block w-5 h-5" />
                  </a>
                </span>
              </span>
            </div>
            <div className="flex flex-col sm:flex-row justify-between py-1" data-testid={`container-transaction-detail-tx-${index}`}>
              <strong>Tx link:</strong>
              <span>
                <a
                  href={`${getConfirmationUrl(leftSideLabel, rightSideLabel)}tx/${paymentId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block sm:inline break-all whitespace-normal text-blue-500"
                  style={{ wordBreak: "break-word", maxWidth: "42ch" }}
                  data-testid={`link-transaction-detail-tx-${index}`}
                >
                  {truncateMiddle(paymentId, 66)}
                </a>
              </span>
            </div>
            {showCancelButton && (
              <div className="flex justify-start mb-0" data-testid={`container-transaction-detail-cancel-${index}`}>
                <button
                  className="bg-black border dark:border-black dark:bg-white dark:text-black text-white py-2 px-4 mt-3"
                  onClick={() => setIsCancelConfirmVisible(true)}
                  data-testid={`btn-transaction-detail-cancel-${index}`}
                >
                  Cancel Transaction
                </button>
              </div>
            )}
          </div>
        </td>
      </tr>

      {isCancelConfirmVisible && (
        <div className="fixed inset-0 flex items-center justify-center z-50" data-testid="modal-transaction-cancel-overlay">
          <div className="absolute inset-0 bg-black opacity-70 z-40"></div>
          <div
            className="relative bg-white dark:bg-pebble p-6 shadow-lg text-center z-50 flex flex-col"
            style={{ width: "400px", height: "200px" }}
            data-testid="container-transaction-cancel-modal"
          >
            <h2 className="text-lg font-normal mb-4 mt-8" data-testid="text-transaction-cancel-title">
              Cancel this transaction?
            </h2>
            <div className="absolute bottom-4 left-0 right-0 flex justify-center space-x-6 mb-5" data-testid="container-transaction-cancel-buttons">
              <button
                className="bg-black dark:bg-white text-white dark:text-black py-1 px-6"
                onClick={handleCancelTransaction}
                data-testid="btn-transaction-cancel-confirm"
              >
                Yes
              </button>
              <button
                className="bg-white dark:bg-pebble text-black dark:text-white py-1 px-6 border border-black dark:border-white"
                onClick={() => setIsCancelConfirmVisible(false)}
                data-testid="btn-transaction-cancel-reject"
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

function showTXNDetails(index: number) {
  const panel = document.getElementById(`panel${index}`);
  panel?.classList.toggle("hidden");
}

export default TransactionRow;

/*
POTENTIAL IMPROVEMENTS:
1. Replace direct DOM manipulation with React state for showing/hiding details
2. Add proper animation for expanding/collapsing details
3. Implement proper error handling for cancelation with user feedback
4. Add loading state for cancel button during API request
5. Add confirmation toast messages after successful cancellation
6. Implement proper focus management for accessibility when details expand
7. Add keyboard navigation for order items and their details
8. Consider using a modal component library instead of custom modals
9. Add proper aria attributes for accessibility
10. Implement better mobile responsiveness for details view
11. Add retry mechanism for failed cancel actions
12. Consider adding pagination for large transaction lists
13. Add sorting options for transaction items
14. Implement filtering by transaction status
15. Consider adding search functionality for transaction ID or wallet
*/
